import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useKeyAction } from "hooks/useKeyAction";
import { isFormControlFocused } from "utils/isFormControlFocused/isFormControlFocused";
import { hasOpenModal } from "components/common/Modal/modalStack";
import { computeRoutePdfLength } from "utils/computeRoutePdfLength";
import {
  setNodePositionInRoute,
  insertNodeOnEdgeInRoute,
  deleteNodeFromRoute,
} from "../../utils/routeGeometryMutators";
import type {
  RouteCreationRow,
  RoutesCreationModeV3Result,
  UseRoutesCreationModeV3Args,
  UpdateRouteFieldArgs,
  SetNodePositionArgs,
  InsertNodeOnEdgeArgs,
  InsertNodeOnEdgeResult,
  DeleteNodeArgs,
} from "./types";

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyRow = (routeTypeName: string): RouteCreationRow => ({
  id: newId(),
  routeTypeName,
  name: "",
  breaker: "",
  polylineLength: 0,
  nodes: [],
  edges: [],
  isFinalized: false,
});

// TODO: Refactor this hook later
export const useRoutesCreationModeV3 = ({
  routeTypeOptions,
  creationStep,
  selectedRouteTypeName,
  pageMetersPerPdfPoint,
  onCreationStepChange,
  onSelectedRouteTypeNameChange,
  onSaveAll,
  onCancel,
}: UseRoutesCreationModeV3Args): RoutesCreationModeV3Result => {
  const { t } = useTranslation();

  const [routes, setRoutes] = useState<RouteCreationRow[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [hoverPosition, setHoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDrawingActive, setIsDrawingActive] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isCancelAllModalOpen, setIsCancelAllModalOpen] = useState(false);

  const dropActiveOrphanIfNeeded = useCallback(() => {
    if (activeIndex < 0 || activeIndex >= routes.length) return;
    const activeRow = routes[activeIndex];
    if (activeRow.isFinalized) return;
    if (activeRow.nodes.length >= 2) return;

    const willBeEmpty = routes.length === 1;
    setRoutes((prev) => prev.filter((_, idx) => idx !== activeIndex));
    if (willBeEmpty) {
      setActiveIndex(-1);
      onCreationStepChange("group-selection");
    } else {
      setActiveIndex(Math.max(0, activeIndex - 1));
    }
  }, [activeIndex, routes, onCreationStepChange]);

  const finalizeActiveRoute = useCallback(() => {
    if (activeIndex < 0 || activeIndex >= routes.length) return;
    const activeRow = routes[activeIndex];
    if (activeRow.isFinalized) return;

    if (activeRow.nodes.length < 2) {
      dropActiveOrphanIfNeeded();
    } else {
      setRoutes((prev) =>
        prev.map((row, idx) =>
          idx === activeIndex ? { ...row, isFinalized: true } : row,
        ),
      );
    }
    setIsDrawingActive(false);
    setHoverPosition(null);
  }, [activeIndex, routes, dropActiveOrphanIfNeeded]);

  const toggleDrawingActive = useCallback(() => {
    if (!isDrawingActive) {
      setIsDrawingActive(true);
      return;
    }
    // Toggling OFF finalizes the active route (or drops it if it's a 1-node orphan).
    // The explicit setters below also cover the edge case where no row has been started.
    finalizeActiveRoute();
    setIsDrawingActive(false);
    setHoverPosition(null);
  }, [isDrawingActive, finalizeActiveRoute]);

  const resetState = useCallback(() => {
    setRoutes([]);
    setActiveIndex(-1);
    setHoverPosition(null);
    setIsDrawingActive(false);
    setSelectedNodeId(null);
    setIsSaving(false);
    setIsDiscardModalOpen(false);
    setIsCancelAllModalOpen(false);
  }, []);

  const handleStartDrawing = useCallback(() => {
    onSelectedRouteTypeNameChange(selectedRouteTypeName.trim());
    setRoutes([]);
    setActiveIndex(-1);
    setHoverPosition(null);
    setIsDrawingActive(true);
    onCreationStepChange("adding-elements");
  }, [
    onCreationStepChange,
    selectedRouteTypeName,
    onSelectedRouteTypeNameChange,
  ]);

  const handleCancelDrawing = useCallback(() => {
    if (routes.length === 0) {
      onCreationStepChange("group-selection");
      return;
    }
    setIsCancelAllModalOpen(true);
  }, [routes.length, onCreationStepChange]);

  const handleBack = useCallback(() => {
    if (creationStep === "group-selection" || routes.length === 0) {
      onCancel();
      return;
    }
    setIsDiscardModalOpen(true);
  }, [creationStep, routes.length, onCancel]);

  const handleConfirmDiscard = useCallback(() => {
    setIsDiscardModalOpen(false);
    onCancel();
  }, [onCancel]);

  const handleCancelDiscard = useCallback(() => {
    setIsDiscardModalOpen(false);
  }, []);

  const handleConfirmCancelAll = useCallback(() => {
    setIsCancelAllModalOpen(false);
    setRoutes([]);
    setActiveIndex(-1);
    setHoverPosition(null);
    setIsDrawingActive(false);
    onCreationStepChange("group-selection");
  }, [onCreationStepChange]);

  const handleCloseCancelAll = useCallback(() => {
    setIsCancelAllModalOpen(false);
  }, []);

  const appendNodeToActiveRoute = useCallback(
    (pos: { x: number; y: number }) => {
      if (!isDrawingActive) return;

      const activeRow = activeIndex >= 0 ? routes[activeIndex] : null;

      // No active row or it's been finalized → start a new route.
      if (!activeRow || activeRow.isFinalized) {
        const newRow: RouteCreationRow = {
          ...createEmptyRow(selectedRouteTypeName),
          nodes: [{ id: 1, x: pos.x, y: pos.y }],
          edges: [],
          polylineLength: 0,
        };
        setRoutes((prev) => [...prev, newRow]);
        setActiveIndex(routes.length);
        return;
      }

      const lastNode = activeRow.nodes[activeRow.nodes.length - 1];
      const newNodeId = lastNode ? lastNode.id + 1 : 1;
      const updatedNodes = [
        ...activeRow.nodes,
        { id: newNodeId, x: pos.x, y: pos.y },
      ];
      const updatedEdges = lastNode
        ? [...activeRow.edges, { from: lastNode.id, to: newNodeId }]
        : activeRow.edges;
      const updatedPolylineLength = computeRoutePdfLength({
        nodes: updatedNodes,
        edges: updatedEdges,
      });
      const updatedRow: RouteCreationRow = {
        ...activeRow,
        nodes: updatedNodes,
        edges: updatedEdges,
        polylineLength: updatedPolylineLength,
      };
      setRoutes((prev) =>
        prev.map((row, idx) => (idx === activeIndex ? updatedRow : row)),
      );
    },
    [activeIndex, routes, selectedRouteTypeName, isDrawingActive],
  );

  const removeLastNodeFromActiveRoute = useCallback(() => {
    setRoutes((prev) => {
      if (activeIndex < 0 || activeIndex >= prev.length) return prev;
      const activeRow = prev[activeIndex];
      if (activeRow.isFinalized) return prev;
      if (activeRow.nodes.length === 0) return prev;

      const updatedNodes = activeRow.nodes.slice(0, -1);
      const updatedEdges = activeRow.edges.slice(
        0,
        Math.max(0, updatedNodes.length - 1),
      );
      const updatedPolylineLength = computeRoutePdfLength({
        nodes: updatedNodes,
        edges: updatedEdges,
      });
      const updatedRow: RouteCreationRow = {
        ...activeRow,
        nodes: updatedNodes,
        edges: updatedEdges,
        polylineLength: updatedPolylineLength,
      };
      return prev.map((row, idx) => (idx === activeIndex ? updatedRow : row));
    });
  }, [activeIndex]);

  const setNodePosition = useCallback(
    ({ routeIndex, nodeId, pos }: SetNodePositionArgs) => {
      setRoutes((prev) => {
        if (routeIndex < 0 || routeIndex >= prev.length) return prev;
        const targetRow = prev[routeIndex];
        const updated = setNodePositionInRoute({
          geometry: { nodes: targetRow.nodes, edges: targetRow.edges },
          nodeId,
          pos,
        });
        const updatedRow: RouteCreationRow = {
          ...targetRow,
          nodes: updated.nodes,
          edges: updated.edges,
          polylineLength: updated.polylineLength,
        };
        return prev.map((row, idx) => (idx === routeIndex ? updatedRow : row));
      });
    },
    [],
  );

  const insertNodeOnEdge = useCallback(
    ({
      routeIndex,
      edgeIndex,
      pos,
    }: InsertNodeOnEdgeArgs): InsertNodeOnEdgeResult | null => {
      if (routeIndex < 0 || routeIndex >= routes.length) return null;
      const targetRow = routes[routeIndex];
      // Computed outside the state updater: the caller needs the new id right away to select and drag it.
      const insertResult = insertNodeOnEdgeInRoute({
        geometry: { nodes: targetRow.nodes, edges: targetRow.edges },
        edgeIndex,
        pos,
      });
      if (!insertResult) return null;
      const updatedRow: RouteCreationRow = {
        ...targetRow,
        nodes: insertResult.geometry.nodes,
        edges: insertResult.geometry.edges,
        polylineLength: insertResult.geometry.polylineLength,
      };
      setRoutes(
        routes.map((row, idx) => (idx === routeIndex ? updatedRow : row)),
      );
      return { newNodeId: insertResult.newNodeId };
    },
    [routes],
  );

  const deleteNode = useCallback(({ routeIndex, nodeId }: DeleteNodeArgs) => {
    setRoutes((prev) => {
      if (routeIndex < 0 || routeIndex >= prev.length) return prev;
      const targetRow = prev[routeIndex];
      const updated = deleteNodeFromRoute({
        geometry: { nodes: targetRow.nodes, edges: targetRow.edges },
        nodeId,
      });
      if (!updated) return prev;
      const updatedRow: RouteCreationRow = {
        ...targetRow,
        nodes: updated.nodes,
        edges: updated.edges,
        polylineLength: updated.polylineLength,
      };
      return prev.map((row, idx) => (idx === routeIndex ? updatedRow : row));
    });
    setSelectedNodeId((current) => (current === nodeId ? null : current));
  }, []);

  const finalizeFromKeyboard = useCallback(
    (event: KeyboardEvent) => {
      if (isFormControlFocused(event)) return;
      // Stop browsers from re-triggering the focused Drawing toggle button
      // via Enter's default click action.
      event.preventDefault();
      finalizeActiveRoute();
    },
    [finalizeActiveRoute],
  );

  const isKeyboardFinalizeDisabled =
    creationStep !== "adding-elements" ||
    !isDrawingActive ||
    isDiscardModalOpen ||
    isCancelAllModalOpen;

  useKeyAction({
    code: "Enter",
    callback: finalizeFromKeyboard,
    options: { isDisabled: isKeyboardFinalizeDisabled },
  });

  useKeyAction({
    code: "Escape",
    callback: finalizeFromKeyboard,
    options: { isDisabled: isKeyboardFinalizeDisabled },
  });

  const isDeleteSelectedNodeDisabled =
    creationStep !== "adding-elements" ||
    isDrawingActive ||
    selectedNodeId === null ||
    activeIndex < 0 ||
    isDiscardModalOpen ||
    isCancelAllModalOpen;

  const deleteSelectedNodeFromKeyboard = useCallback(
    (event: KeyboardEvent) => {
      // The calibration modal can open on top of creation mode; the key is its then.
      if (isFormControlFocused(event) || hasOpenModal()) return;
      if (selectedNodeId === null) return;
      if (activeIndex < 0) return;
      deleteNode({ routeIndex: activeIndex, nodeId: selectedNodeId });
    },
    [selectedNodeId, activeIndex, deleteNode],
  );

  useKeyAction({
    code: "KeyD",
    callback: deleteSelectedNodeFromKeyboard,
    options: { isDisabled: isDeleteSelectedNodeDisabled },
  });

  useKeyAction({
    code: "Backspace",
    callback: deleteSelectedNodeFromKeyboard,
    options: { isDisabled: isDeleteSelectedNodeDisabled },
  });

  useKeyAction({
    code: "Delete",
    callback: deleteSelectedNodeFromKeyboard,
    options: { isDisabled: isDeleteSelectedNodeDisabled },
  });

  const removeRoute = useCallback(
    (index: number) => {
      const nextRoutes = routes.filter((_, idx) => idx !== index);
      setRoutes(nextRoutes);

      if (nextRoutes.length === 0) {
        setActiveIndex(-1);
        onCreationStepChange("group-selection");
        return;
      }

      setActiveIndex((current) => {
        if (current === index) return Math.max(0, index - 1);
        if (current > index) return current - 1;
        return current;
      });
    },
    [routes, onCreationStepChange],
  );

  const selectRoute = useCallback(
    (index: number) => {
      if (!isDrawingActive) {
        setActiveIndex(index);
        return;
      }
      // Switching rows while drawing finalizes the current row (or drops it if it's a
      // 1-node orphan). If an orphan before the target is dropped, the target shifts down.
      const droppedOrphanBeforeTarget =
        activeIndex >= 0 &&
        activeIndex < routes.length &&
        activeIndex < index &&
        !routes[activeIndex].isFinalized &&
        routes[activeIndex].nodes.length < 2;
      finalizeActiveRoute();
      setActiveIndex(droppedOrphanBeforeTarget ? index - 1 : index);
    },
    [isDrawingActive, activeIndex, routes, finalizeActiveRoute],
  );

  const updateRouteField = useCallback(
    <K extends keyof RouteCreationRow>({
      index,
      field,
      value,
    }: UpdateRouteFieldArgs<K>) => {
      setRoutes((prev) =>
        prev.map((row, idx) =>
          idx === index ? { ...row, [field]: value } : row,
        ),
      );
    },
    [],
  );

  const handleSaveAll = useCallback(async () => {
    if (routes.length === 0) return;

    if (pageMetersPerPdfPoint <= 0) {
      toast.error(t("smartSurveyor.routesCreation.calibrationRequired"));
      return;
    }

    const finalizedRoutes = routes.map((row) =>
      row.isFinalized || row.nodes.length >= 2
        ? { ...row, isFinalized: true }
        : row,
    );

    const incompleteRoute = finalizedRoutes.find(
      (row) =>
        row.nodes.length < 2 ||
        row.edges.length === 0 ||
        row.polylineLength <= 0,
    );

    if (incompleteRoute) {
      toast.error(t("smartSurveyor.routesCreation.routeIncomplete"));
      return;
    }

    const missingCableType = finalizedRoutes.find(
      (row) => row.routeTypeName.trim().length === 0,
    );
    if (missingCableType) {
      toast.error(t("smartSurveyor.routesCreation.routeTypeRequired"));
      return;
    }

    setRoutes(finalizedRoutes);
    setIsSaving(true);
    try {
      await onSaveAll(finalizedRoutes);
    } finally {
      setIsSaving(false);
    }
  }, [routes, pageMetersPerPdfPoint, onSaveAll, t]);

  // Node ids restart at 1 in every route, so a selection must not outlive the route it was made in.
  useEffect(() => {
    setSelectedNodeId(null);
  }, [activeIndex]);

  return {
    routes,
    activeIndex,
    setActiveIndex,
    selectRoute,
    removeRoute,
    appendNodeToActiveRoute,
    removeLastNodeFromActiveRoute,
    finalizeActiveRoute,
    setNodePosition,
    insertNodeOnEdge,
    deleteNode,
    selectedNodeId,
    setSelectedNodeId,
    updateRouteField,
    hoverPosition,
    setHoverPosition,
    isDrawingActive,
    toggleDrawingActive,
    resetState,
    routeTypeOptions,
    selectedRouteTypeName,
    setSelectedRouteTypeName: onSelectedRouteTypeNameChange,
    creationStep,
    handleStartDrawing,
    handleCancelDrawing,
    handleBack,
    onCancel,
    isSaving,
    handleSaveAll,
    isDiscardModalOpen,
    handleConfirmDiscard,
    handleCancelDiscard,
    isCancelAllModalOpen,
    handleConfirmCancelAll,
    handleCloseCancelAll,
    pageMetersPerPdfPoint,
  };
};
